// Exact money arithmetic. All amounts are integer pence; quantities are integer thousandths.

export const QTY_ONE = 1000;

/** Round half away from zero for an integer numerator / positive integer denominator. */
function divRound(num: number, den: number): number {
  const sign = num < 0 ? -1 : 1;
  const abs = Math.abs(num);
  return sign * Math.floor((abs * 2 + den) / (den * 2));
}

export function lineNet(qtyMilli: number, unitPence: number): number {
  return divRound(qtyMilli * unitPence, QTY_ONE);
}

export function vatOn(netPence: number, rateBp: number): number {
  return divRound(netPence * rateBp, 10_000);
}

/** Parse a user-entered pounds string ("12", "12.5", "£1,234.56", "-3.20") into pence without floats. */
export function parsePounds(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) return null;
  let s = String(input).trim().replace(/[£,\s]/g, "");
  if (s === "") return null;
  let neg = false;
  if (s.startsWith("(") && s.endsWith(")")) { neg = true; s = s.slice(1, -1); }
  if (s.startsWith("-")) { neg = !neg; s = s.slice(1); } else if (s.startsWith("+")) s = s.slice(1);
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(s);
  if (!m || (m[1] === "" && (m[2] ?? "") === "")) return null;
  const pence = Number(m[1] || "0") * 100 + Number((m[2] ?? "").padEnd(2, "0") || "0");
  if (!Number.isSafeInteger(pence)) return null;
  return neg ? -pence : pence;
}

/** Parse a quantity like "1", "1.5", "0.25" into thousandths. */
export function parseQty(input: string | number): number | null {
  const s = String(input).trim();
  const m = /^(\d+)(?:\.(\d{1,3}))?$/.exec(s);
  if (!m) return null;
  return Number(m[1]) * QTY_ONE + Number((m[2] ?? "").padEnd(3, "0") || "0");
}

export function formatQty(qtyMilli: number): string {
  const whole = Math.trunc(qtyMilli / QTY_ONE);
  const frac = Math.abs(qtyMilli % QTY_ONE);
  return frac === 0 ? String(whole) : `${whole}.${String(frac).padStart(3, "0").replace(/0+$/, "")}`;
}

export function penceToPoundsString(pence: number): string {
  const neg = pence < 0;
  const abs = Math.abs(pence);
  return `${neg ? "-" : ""}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

export function formatGBP(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) return "—";
  const neg = pence < 0;
  const abs = Math.abs(pence);
  const pounds = Math.floor(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${neg ? "−" : ""}£${pounds}.${String(abs % 100).padStart(2, "0")}`;
}

export interface PricedLine { qtyMilli: number; unitPricePence: number; vatRateBp: number }

export function totalsFor(lines: PricedLine[], vatRegistered: boolean) {
  let net = 0;
  let vat = 0;
  const perLine = lines.map((l) => {
    const n = lineNet(l.qtyMilli, l.unitPricePence);
    const v = vatRegistered ? vatOn(n, l.vatRateBp) : 0;
    net += n;
    vat += v;
    return { net: n, vat: v };
  });
  return { net, vat, total: net + vat, perLine };
}

/** Split `amount` across `weights` proportionally; remainder goes to the largest weight so the sum is exact. */
export function allocateProportionally(amount: number, weights: number[]): number[] {
  const totalW = weights.reduce((a, b) => a + b, 0);
  if (totalW === 0) return weights.map((_, i) => (i === 0 ? amount : 0));
  const parts = weights.map((w) => Math.floor((amount * w) / totalW));
  let rem = amount - parts.reduce((a, b) => a + b, 0);
  const order = weights.map((w, i) => [w, i] as const).sort((a, b) => b[0] - a[0]);
  for (let k = 0; rem > 0; k = (k + 1) % order.length, rem--) parts[order[k][1]]++;
  return parts;
}
